/**
 * videoUrl.js — Parse YouTube / Vimeo / Instagram URLs into embed + thumbnail data.
 *
 * Derived (not stored) so an edited URL never leaves stale video metadata.
 * Vimeo/Instagram thumbnails aren't derivable from the ID alone — those come
 * from the oEmbed/OG proxy at save time (wiki_articles.thumbnail_url).
 */

const YT_ID = /^[A-Za-z0-9_-]{11}$/;
const HEX_HASH = /^[0-9a-f]{6,20}$/i;
const IG_SHORTCODE = /^[A-Za-z0-9_-]{5,24}$/;

// Base domains we trust to serve stored thumbnail_url values (blocks off-domain
// image beacons — see isTrustedThumbnailHost). Matched as an exact host or a
// dot-anchored subdomain, so lookalikes like evilcdninstagram.com are rejected.
const THUMB_HOSTS = [
  'ytimg.com', 'youtube.com',
  'vimeocdn.com',
  'cdninstagram.com', 'fbcdn.net', 'instagram.com',
];

/**
 * @returns {null | {
 *   provider: 'youtube'|'vimeo'|'instagram', videoId: string,
 *   embedUrl: string, autoplayUrl: string, thumbnailUrl: string|null,
 *   watchUrl: string, aspect: number   // width/height for the player box
 * }}
 */
export function parseVideoUrl(raw) {
  if (!raw) return null;
  let u;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  const host = u.hostname.replace(/^www\./i, '').toLowerCase();
  const parts = u.pathname.split('/').filter(Boolean);

  // ── YouTube ──
  let ytId = null;
  if (host === 'youtu.be') {
    ytId = parts[0];
  } else if (['youtube.com', 'm.youtube.com', 'youtube-nocookie.com'].includes(host)) {
    if (u.pathname === '/watch') ytId = u.searchParams.get('v');
    else if (['shorts', 'embed', 'live', 'v'].includes(parts[0])) ytId = parts[1];
  }
  if (ytId && YT_ID.test(ytId)) {
    const base = `https://www.youtube-nocookie.com/embed/${ytId}`;
    return {
      provider: 'youtube',
      videoId: ytId,
      embedUrl: `${base}?rel=0`,
      autoplayUrl: `${base}?rel=0&autoplay=1`,
      thumbnailUrl: `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`,
      watchUrl: `https://www.youtube.com/watch?v=${ytId}`,
      aspect: 16 / 9,
    };
  }

  // ── Vimeo ──
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    // showcase/album/ondemand aren't single playable videos → treat as links
    if (['showcase', 'album', 'ondemand'].includes(parts[0])) {
      return null;
    }
    // Pick the id by path structure. For the bare form vimeo.com/{id}[/{hash}]
    // the id is the FIRST segment (an all-numeric privacy hash after it must not
    // be mistaken for the id); for channels/groups/video/{id} the id is the last
    // numeric segment.
    let idPart = null;
    let idIdx = -1;
    if (/^\d{6,}$/.test(parts[0])) {
      idPart = parts[0];
      idIdx = 0;
    } else {
      for (let i = parts.length - 1; i >= 0; i--) {
        if (/^\d{6,}$/.test(parts[i])) { idPart = parts[i]; idIdx = i; break; }
      }
    }
    if (idPart) {
      const nextSeg = parts[idIdx + 1];
      const pathHash = nextSeg && HEX_HASH.test(nextSeg) ? nextSeg : null;
      const qHash = u.searchParams.get('h');
      const hash = pathHash || (qHash && HEX_HASH.test(qHash) ? qHash : null);
      const h = hash ? `h=${hash}&` : '';
      const base = `https://player.vimeo.com/video/${idPart}`;
      return {
        provider: 'vimeo',
        videoId: idPart,
        embedUrl: `${base}?${h}dnt=1`,
        autoplayUrl: `${base}?${h}dnt=1&autoplay=1`,
        thumbnailUrl: null,
        watchUrl: `https://vimeo.com/${idPart}${hash ? `/${hash}` : ''}`,
        aspect: 16 / 9,
      };
    }
  }

  // ── Instagram (posts, reels, IGTV) ──
  if (host === 'instagram.com' || host === 'instagr.am') {
    const kindIdx = parts.findIndex((p) => ['p', 'reel', 'reels', 'tv'].includes(p));
    if (kindIdx !== -1) {
      const code = parts[kindIdx + 1];
      if (code && IG_SHORTCODE.test(code)) {
        // /embed/captioned renders the post + caption; frameable by design.
        const base = `https://www.instagram.com/p/${code}/embed/captioned`;
        return {
          provider: 'instagram',
          videoId: code,
          embedUrl: base,
          autoplayUrl: base, // IG embeds have no autoplay param
          thumbnailUrl: null,
          watchUrl: `https://www.instagram.com/p/${code}/`,
          aspect: 1, // IG embeds are ~square/portrait; a square box reads best
        };
      }
    }
  }

  return null;
}

/** True if the URL is a recognized, embeddable video/post link. */
export function isVideoUrl(raw) {
  return parseVideoUrl(raw) !== null;
}

/**
 * True if a stored thumbnail_url is served by a provider CDN we trust.
 * Prevents an author-supplied off-domain image from beaconing every viewer.
 */
export function isTrustedThumbnailHost(raw) {
  if (!raw) return false;
  try {
    const host = new URL(raw).hostname.replace(/^www\./i, '').toLowerCase();
    return THUMB_HOSTS.some((h) => host === h || host.endsWith('.' + h));
  } catch {
    return false;
  }
}

/** Hostname without www., for generic link cards. Empty string if unparseable. */
export function urlDomain(raw) {
  try {
    return new URL(raw).hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
}
